import { Record, List } from "immutable";
import { DateStringNumber, Nullable } from "common/records";
import { checkDate } from "common/helpers/Date";
import { EstimateLineItemRecord, isMarkupable, isDiscountable } from "common/records/EstimateLineItem";
import { EstimateRecord, markupMultiplier } from "common/records/Estimate";
import { round } from "lodash";
import { AbleProp } from "common/types";

export const estimateLineItemOptionFromJSON = (
  json: Partial<IEstimateLineItemOptionData>,
): EstimateLineItemOptionRecord => {
  const record: IEstimateLineItemOptionRecord = { ...(json as any) };

  // Always wrap — the record types these as List<number>/List<string>, so an absent or explicitly
  // null server value must coerce to an empty List, never survive as null. List(null)/List(undefined)
  // both yield an empty List. A truthy guard here let an explicit server `null` fall through the spread
  // and reach the save-path `.toArray()` (pickOptionFields), which would throw (RSF-25619).
  record.product_option_image_ids = List(json.product_option_image_ids);
  record.product_option_image_urls = List(json.product_option_image_urls);

  if (json.created_at) {
    record.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    record.updated_at = checkDate(json.updated_at);
  }

  return new EstimateLineItemOptionRecord(record);
};

export const calcEstimateLineItemOptionTotals = (
  lineItemOption: EstimateLineItemOptionRecord,
  lineItem: EstimateLineItemRecord,
  estimate: EstimateRecord,
) => {
  const basePrices =
    lineItemOption.uom === "percent"
      ? {
          base_product_price: round(lineItem.base_product_price * (lineItemOption.product_factor / 100), 2),
          base_labor_price: round(lineItem.base_labor_price * (lineItemOption.labor_factor / 100), 2),
        }
      : {
          base_product_price: lineItemOption.base_product_price || 0,
          base_labor_price: lineItemOption.base_labor_price || 0,
        };

  const prices = isMarkupable(lineItem)
    ? {
        product_price: round(basePrices.base_product_price * markupMultiplier(estimate.product_markup), 2),
        labor_price: round(basePrices.base_labor_price * markupMultiplier(estimate.labor_markup), 2),
      }
    : {
        product_price: basePrices.base_product_price,
        labor_price: basePrices.base_labor_price,
      };

  const rawPrice = prices.product_price + prices.labor_price;
  // Round `price` to 2 decimals so it matches the server's clean 2-decimal baseline in the
  // isEstimateDirty `.equals()` check (RSF-25841 — options are compared with a bare `.equals()`
  // with no exclusion list, so a float-sum price reads permanently dirty). `ext_price` stays on
  // the raw sum so totals never drift.
  const price = round(rawPrice, 2);
  const ext_price = round(rawPrice * calculateQuantity(lineItemOption, lineItem), 2);

  return {
    ...basePrices,
    ...prices,
    price,
    ext_price,
  };
};

export const sumEstimateLineItemOptionTotals = (
  lineItemOptions: List<Nullable<EstimateLineItemOptionRecord>>,
  lineItem: EstimateLineItemRecord,
) => {
  return lineItemOptions.reduce(
    (totals, optionRecord) => {
      if (!optionRecord) {
        console.error("Missing required data in redux to sum estimate line item option totals", {
          lineItemOptions: lineItemOptions.toJS(),
          lineItem: lineItem.toJS(),
        });
        return totals;
      }

      if (optionRecord._destroy || optionRecord.pog_type === "cost") {
        return totals;
      }

      const extPrice = optionRecord.ext_price || 0;
      const laborPrice = optionRecord.labor_price || 0;
      const productPrice = optionRecord.product_price || 0;
      const quantity = calculateQuantity(optionRecord, lineItem);
      const discountableTotal = isDiscountable(lineItem) ? extPrice : 0;

      return {
        subtotal: totals.subtotal + extPrice,
        discountable_total: totals.discountable_total + discountableTotal,
        labor_total: totals.labor_total + round(laborPrice * quantity, 2),
        product_total: totals.product_total + round(productPrice * quantity, 2),
      };
    },
    {
      subtotal: 0,
      discountable_total: 0,
      labor_total: 0,
      product_total: 0,
    },
  );
};

export const calculateQuantity = (lineItemOption: EstimateLineItemOptionRecord, lineItem: EstimateLineItemRecord) => {
  if (lineItemOption.charge_type === "per") {
    return round(lineItemOption.quantity, 2);
  } else {
    return round(lineItemOption.quantity * lineItem.quantity, 2);
  }
};

export type ChargeType = "per_quantity" | "per" | "per_ui" | "per_sqft";

export interface IEstimateLineItemOptionData {
  id: number;
  quantity: number;
  name: string;
  description: string;
  ext_price: number;
  uom: string;
  price: number;
  cost: number;
  msrp: number;
  product_option_uuid: string;
  product_option_match_id: string;
  estimate_line_item_id: number;
  product_option_group_id: number;
  product_option_group_name: string;
  product_option_id: Nullable<number>;
  product_option_image_ids: number[];
  product_option_image_urls: string[];
  labor_price: number;
  product_price: number;
  base_labor_price: number;
  base_product_price: number;
  labor_factor: number;
  product_factor: number;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
  charge_type: ChargeType;
  pog_type: string;
  _destroy: Nullable<boolean>;
}

export interface IEstimateLineItemOptionRecord {
  id: number;
  quantity: number;
  name: string;
  description: string;
  ext_price: number;
  uom: string;
  price: number;
  cost: number;
  msrp: number;
  product_option_uuid: string;
  product_option_match_id: string;
  estimate_line_item_id: number;
  product_option_group_id: number;
  product_option_group_name: string;
  product_option_id: Nullable<number>;
  product_option_image_ids: List<number>;
  product_option_image_urls: List<string>;
  labor_price: number;
  product_price: number;
  base_labor_price: number;
  base_product_price: number;
  labor_factor: number;
  product_factor: number;
  created_at: Date;
  updated_at: Date;
  charge_type: ChargeType;
  pog_type: string;
  _destroy: Nullable<boolean>;
}

const defaultEstimateLineItemOptionProps: IEstimateLineItemOptionRecord = {
  id: 0,
  quantity: 0,
  name: "",
  description: "",
  ext_price: 0,
  uom: "",
  price: 0,
  cost: 0,
  msrp: 0,
  product_option_uuid: "",
  product_option_match_id: "",
  estimate_line_item_id: 0,
  product_option_group_id: 0,
  product_option_group_name: "",
  product_option_id: null,
  product_option_image_ids: List<number>(),
  product_option_image_urls: List<string>(),
  labor_price: 0,
  product_price: 0,
  base_labor_price: 0,
  base_product_price: 0,
  labor_factor: 0,
  product_factor: 0,
  created_at: new Date(),
  updated_at: new Date(),
  charge_type: "per_quantity",
  pog_type: "",
  _destroy: null,
};

export class EstimateLineItemOptionRecord
  extends Record(defaultEstimateLineItemOptionProps)
  implements IEstimateLineItemOptionRecord {}
